import { ChevronLeft, ChevronRight } from "lucide-react";

import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { Button } from "@/v2/ui/primitives/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/v2/ui/primitives/select";

export interface DataTablePaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  totalItems: number;
  isLoading?: boolean;
  className?: string;
}

export function DataTablePagination({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  totalItems,
  isLoading = false,
  className,
}: DataTablePaginationProps) {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <div
      className={cn(
        "tw-flex tw-flex-col tw-gap-2 sm:tw-gap-4 tw-px-2 tw-py-2 sm:tw-py-3",
        className
      )}
    >
      {/* Mobile layout - compact single row */}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-1 sm:tw-hidden">
        <div className="tw-flex tw-items-center tw-gap-1 tw-overflow-hidden tw-min-w-0">
          <span className="tw-text-sm tw-text-muted-foreground tw-truncate">
            {isLoading
              ? strings("common.loading")
              : `${strings("common.page")} ${totalPages === 0 ? 0 : currentPage}/${totalPages}`}
          </span>
          <span className="tw-text-sm tw-text-muted-foreground tw-truncate">
            {isLoading ? "" : `${startItem}-${endItem}`}
          </span>
        </div>
        <div className="tw-flex tw-items-center tw-gap-1 tw-flex-shrink-0">
          <Button
            variant="outline"
            size="icon"
            onClick={handlePrevious}
            aria-label={strings("common.previous")}
            disabled={currentPage === 1 || isLoading}
            className="tw-h-8 tw-w-8"
          >
            <ChevronLeft className="tw-h-4 tw-w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleNext}
            aria-label={strings("common.next")}
            disabled={
              currentPage === totalPages || totalPages === 0 || isLoading
            }
            className="tw-h-8 tw-w-8"
          >
            <ChevronRight className="tw-h-4 tw-w-4" />
          </Button>
        </div>
      </div>

      {/* Desktop layout - horizontal with rows selector */}
      <div className="tw-hidden sm:tw-flex tw-items-center tw-justify-between tw-w-full tw-flex-wrap md:tw-flex-nowrap">
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-sm tw-text-muted-foreground tw-whitespace-nowrap sm:tw-hidden">
              {strings("common.rows")}:
            </span>
            <span className="tw-text-sm tw-text-muted-foreground tw-whitespace-nowrap tw-hidden sm:tw-inline">
              {strings("common.rowsPerPage")}:
            </span>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => onPageSizeChange(Number(value))}
              disabled={isLoading}
            >
              <SelectTrigger className="tw-w-[70px] sm:tw-w-[80px] tw-h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="500">500</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <span className="tw-text-sm tw-text-muted-foreground tw-whitespace-nowrap">
            {isLoading
              ? strings("common.loading")
              : `${startItem}-${endItem} ${strings("common.of")} ${totalItems}`}
          </span>
        </div>

        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
          <span className="tw-text-sm tw-text-muted-foreground tw-whitespace-nowrap">
            {isLoading
              ? strings("common.loading")
              : `${strings("common.page")} ${totalPages === 0 ? 0 : currentPage} ${strings("common.of")} ${totalPages}`}
          </span>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handlePrevious}
              aria-label={strings("common.previous")}
              disabled={currentPage === 1 || isLoading}
              className="tw-h-8 tw-w-8"
            >
              <ChevronLeft className="tw-h-4 tw-w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleNext}
              aria-label={strings("common.next")}
              disabled={
                currentPage === totalPages || totalPages === 0 || isLoading
              }
              className="tw-h-8 tw-w-8"
            >
              <ChevronRight className="tw-h-4 tw-w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
